import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CaptchaService } from '../../tokens';
import { CaptchaAction } from '../../types/captcha';

/**
 * A wrapper service that adds processing states for validating or tokenizing CAPTCHA data.
 */
@Injectable({ providedIn: 'root' })
export class DcCaptchaService {

    private readonly _captchaService = inject(CaptchaService);

    private readonly _isValidatingSubject = new BehaviorSubject(false);
    readonly isValidating$ = this._isValidatingSubject.asObservable();

    private readonly _isRetrievingTokenSubject = new BehaviorSubject(false);
    readonly isRetrieving$ = this._isRetrievingTokenSubject.asObservable();

    /**
     * Validate the currently collected user-app interaction. The data is processed and
     * a score is derived with the API deciding the acceptable score.
     * @param action A description of what is being validated, e.g. 'payment_add'.
     * Actions might contain only alphanumeric characters, slashes, and underscores.
     * Actions must not be user-specific.
     * @returns True if the API approves of the score, and False if it does not.
     */
    validate(action: CaptchaAction): Observable<boolean> {
        this._isValidatingSubject.next(true);
        return this._captchaService.validate(action).pipe(
            catchError(() => of(false)),
            finalize(() => this._isValidatingSubject.next(false))
        );
    }

  /**
   * Build a token from the currently collected user-app interaction.
   * @param action A description of what is being validated, e.g. 'payment_add'.
   * Actions might contain only alphanumeric characters, slashes, and underscores.
   * Actions must not be user-specific.
   * @returns The token corresponding to the currently collected user-app interaction.
   */
  tokenize(action: CaptchaAction): Observable<string> {
        this._isRetrievingTokenSubject.next(true);
        return this._captchaService.tokenize(action).pipe(
            catchError(() => of('')),
            finalize(() => this._isRetrievingTokenSubject.next(false))
        );
    }

}
